import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Box, Grid, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/Place";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
    getLocationsByCompanyId,
    createLocation,
    updateLocation,
    deleteLocation,
} from "./api";
import { LocationData, CreateLocationParams, ApiResponse } from "./interface";
import { getErrorMessage } from "../../../utils/error";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";

const Location: React.FC = () => {
    const { company_id } = useParams();
    const location = useLocation();
    const company_name = location.state?.company_name || "Locations";
    const [locations, setLocations] = useState<LocationData[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [editLocation, setEditLocation] = useState<LocationData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        locationName: Yup.string().required("Location name is required"),
    });

    const columns: GridColDef[] = [
        { field: "location_name", headerName: "Location Name", flex: 1 },
        { field: "latitude", headerName: "Latitude", flex: 1 },
        { field: "longitude", headerName: "Longitude", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
        },
    ];

    useEffect(() => {
        const fetchLocations = async () => {
            if (!company_id) return;
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<LocationData[]> = await getLocationsByCompanyId(company_id);
                if (res.status && res.data) {
                    setLocations(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch locations.");
                }
            } catch (err) {
                errorMessage(getErrorMessage(err));
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };
        fetchLocations();
    }, [company_id]);

    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditLocation(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditLocation(params.row as LocationData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const confirmDialog = async (
        values: { locationName: string },
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editLocation) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newLocation: CreateLocationParams = {
                location_name: values.locationName,
                address: values?.address,
                latitude: Number(values?.latitude),
                longitude: Number(values?.longitude),
                company_id: company_id!,
            };
            const res = await createLocation(newLocation);
            if (res.status && res.data) {
                const location = {
                    location_id: res.data?.location_id,
                    location_name: values.locationName,
                    address: values?.address,
                    latitude: Number(values?.latitude),
                    longitude: Number(values?.longitude),
                    company_id: company_id!,
                };
                setLocations((prev) => [...prev, location]);
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to create location.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedData = {
                location_name: values.locationName,
                address: values?.address,
                latitude: Number(values?.latitude),
                longitude: Number(values?.longitude),
                location_id: editLocation?.location_id!,
                company_id: company_id!,
            };
            const res = await updateLocation(updatedData);
            if (res.status) {
                setLocations((prev) =>
                    prev.map((loc) =>
                        loc.location_id === editLocation?.location_id ? { ...loc, ...updatedData } : loc
                    )
                );
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to update location.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const res = await deleteLocation(itemToDelete.row.location_id);
                if (res.status) {
                    setLocations((prev) =>
                        prev.filter((loc) => loc.location_id !== itemToDelete.row.location_id)
                    );
                    successMessage(res.message);
                } else {
                    errorMessage(res.message);
                }
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
        } finally {
            setLoading((prev) => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box className="m-a-20">
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "30px" }}
                    >
                        <Link to="/company">Company</Link>
                        <Link to={`/company`}>
                            {company_name}
                        </Link>
                        <Link to={`/company/${company_id}/locations`} state={{ company_name }}>
                            Locations
                        </Link>
                    </Breadcrumbs>
                    <div className="d-flex justify-between w-100">
                        <TextField
                            className="w-25"
                            label="Search"
                            variant="outlined"
                            value={searchQuery}
                            size="small"
                            startIcon={<SearchIcon />}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Button
                            label="Add"
                            variant="contained"
                            color="primary"
                            onClick={openDialog}
                        />
                    </div>
                    <Box className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                        <GridTable
                            rows={locations}
                            columns={columns}
                            pageSize={10}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            getRowId={(row) => row.location_id}
                        />
                    </Box>
                    <Formik
                        initialValues={{
                            locationName: editLocation?.location_name || "",
                            address: editLocation?.address || "",
                            latitude: editLocation?.latitude || "",
                            longitude: editLocation?.longitude || "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={confirmDialog}
                        enableReinitialize
                    >
                        {({ values, handleChange, handleSubmit, errors, touched, resetForm }) => (
                            <Dialog
                                open={isDialogOpen}
                                onClose={() => closeDialog(resetForm)}
                                onConfirm={handleSubmit}
                                title={editLocation ? "Edit Location" : "Add New Location"}
                                confirmButtonText={editLocation ? "Update" : "Add"}
                                cancelButtonText="Cancel"
                                icon={<PlaceIcon />}
                                message={
                                    <Box padding={4}>
                                        {(loading.create || loading.update) ? (
                                            <Loader color="primary" size={40} />
                                        ) : (
                                            <Form>
                                                <Grid container spacing={2}>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="locationName"
                                                            name="locationName"
                                                            label="Location Name"
                                                            fullWidth
                                                            value={values.locationName}
                                                            onChange={handleChange}
                                                            required
                                                            error={Boolean(touched.locationName && errors.locationName)}
                                                            helperText={touched.locationName && errors.locationName}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="address"
                                                            name="address"
                                                            label="Address"
                                                            fullWidth
                                                            value={values.address}
                                                            onChange={handleChange}
                                                            error={Boolean(touched.address && errors.address)}
                                                            helperText={touched.address && errors.address}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="latitude"
                                                            name="latitude"
                                                            label="Latitude"
                                                            type="number"
                                                            fullWidth
                                                            value={values.latitude}
                                                            onChange={handleChange}
                                                            error={Boolean(touched.latitude && errors.latitude)}
                                                            helperText={touched.latitude && errors.latitude}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="longitude"
                                                            name="longitude"
                                                            label="Longitude"
                                                            type="number"
                                                            fullWidth
                                                            value={values.longitude}
                                                            onChange={handleChange}
                                                            error={Boolean(touched.longitude && errors.longitude)}
                                                            helperText={touched.longitude && errors.longitude}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Form>
                                        )}
                                    </Box>
                                }
                            />
                        )}
                    </Formik>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Location"
                confirmButtonText="Confirm"
                cancelButtonText="Cancel"
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this location?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Location;
